'use client';

import { c as _c } from "react/compiler-runtime";
import { useCoupon, useCouponId, useCouponIndices, useCouponType } from '@svs/nova-poolbetting/core/nextjs/coupon';
import type { CouponIndex } from '@svs/nova-poolbetting/schema/coupon';
import type { Draw } from '@svs/nova-poolbetting/schema/draw';
import type { BettingEvent, BettingEventId, EventNumber, EventType, SportType } from '@svs/nova-poolbetting/schema/sport';
import { ClearCouponButton } from '@svs/nova-poolbetting/ui/Coupon';
import { HorizontalScrollArea } from '@svs/nova-poolbetting/ui/HorizontalScrollArea';
import { useMediaQueryAbove } from '@svs/ui/media-query/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { type ReactNode, useEffect, useRef } from 'react';
import { CouponPixButtonClientContainer } from './CouponPixButtonClientContainer';
import { CouponRow } from './CouponRow';
type CouponRowContainerProps = {
  events: Draw['events'];
  sportType: SportType;
  bettingEvents: Record<BettingEventId, BettingEvent>;
  defaultEventType: EventType;
};

/**
 * Component responsible for rendering the outcomes for a specific event. It uses product, eventType and sportType to determine the amount of outcomes.
 *
 * @param root0 props for the component
 * @param root0.events array of events
 * @param root0.sportType sport type for the events
 * @param root0.bettingEvents record of betting events
 * @param root0.defaultEventType fallback event type
 * @returns component that renders outcomes for specific events
 */
export function CouponRowContainer(t0) {
  const $ = _c(53);
  const {
    events,
    sportType,
    bettingEvents,
    defaultEventType
  } = t0;
  const isLargeScreen = useMediaQueryAbove("md");
  const [couponId, couponIndex] = useCouponId();
  let t1;
  if ($[0] !== couponId || $[1] !== couponIndex) {
    t1 = {
      couponId,
      couponIndex
    };
    $[0] = couponId;
    $[1] = couponIndex;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const couponType = useCouponType(t1);
  const isSingleRows = couponType === "singlerows";
  let t2;
  if ($[3] !== couponId) {
    t2 = {
      couponId
    };
    $[3] = couponId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const couponIndices = useCouponIndices(t2);
  let t3;
  if ($[5] !== couponId || $[6] !== couponIndex) {
    t3 = {
      couponId,
      couponIndex
    };
    $[5] = couponId;
    $[6] = couponIndex;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const coupon = useCoupon(t3);
  const router = useRouter();
  const searchParams = useSearchParams();
  const scrollRefElement = useRef(null);
  let t4;
  let t5;
  if ($[8] !== coupon || $[9] !== couponIndex || $[10] !== couponIndices || $[11] !== router || $[12] !== searchParams) {
    t4 = () => {
      if (!coupon || !couponIndices.includes(couponIndex)) {
        const qs = new URLSearchParams(searchParams.toString());
        if (couponIndices.includes(couponIndex - 1 as CouponIndex)) {
          qs.set("coupon", JSON.stringify(couponIndex - 1));
        } else {
          qs.delete("coupon");
        }
        setTimeout(() => {
          router.push(`?${qs.toString()}`);
        }, 0);
      }
    };
    t5 = [coupon, couponIndex, couponIndices, router, searchParams];
    $[8] = coupon;
    $[9] = couponIndex;
    $[10] = couponIndices;
    $[11] = router;
    $[12] = searchParams;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  if (!coupon) {
    return;
  }
  let t6;
  if ($[15] !== couponIndex || $[16] !== couponIndices || $[17] !== isLargeScreen) {
    t6 = isLargeScreen ? couponIndices : [couponIndex];
    $[15] = couponIndex;
    $[16] = couponIndices;
    $[17] = isLargeScreen;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const couponIndicesToRender = t6;
  const hasMultipleCoupons = couponIndices.length > 1;
  let t7;
  if ($[19] !== isLargeScreen || $[20] !== router || $[21] !== searchParams) {
    t7 = t8 => {
      const {
        eventNumber
      } = t8;
      const qs_0 = new URLSearchParams(searchParams.toString());
      qs_0.set("event", eventNumber.toString());
      if (!isLargeScreen) {
        qs_0.set("modal", "stats");
      }
      router.push(`?${qs_0.toString()}`, {
        scroll: false
      });
    };
    $[19] = isLargeScreen;
    $[20] = router;
    $[21] = searchParams;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleClick = t7;
  let t8;
  if ($[23] !== bettingEvents || $[24] !== coupon || $[25] !== couponIndex || $[26] !== couponIndicesToRender || $[27] !== defaultEventType || $[28] !== events || $[29] !== handleClick || $[30] !== hasMultipleCoupons || $[31] !== isSingleRows || $[32] !== sportType) {
    let t9;
    if ($[34] !== bettingEvents || $[35] !== coupon || $[36] !== couponIndex || $[37] !== couponIndicesToRender.length || $[38] !== defaultEventType || $[39] !== events || $[40] !== handleClick || $[41] !== hasMultipleCoupons || $[42] !== isSingleRows || $[43] !== sportType) {
      t9 = couponIndexToRender => <CouponRow key={couponIndexToRender} couponIndex={couponIndexToRender} events={events} sportType={sportType} bettingEvents={bettingEvents} defaultEventType={defaultEventType} hasMultipleCoupons={hasMultipleCoupons} selectedCouponIndex={couponIndex} handleClick={handleClick} drawNumber={coupon.drawNumber} couponRowFooter={couponIndicesToRender.length > 1 && isSingleRows ? <div data-coupon-row-footer={true} className={`${couponIndexToRender === couponIndex ? "lg:bg-secondary-variant" : ""} bg-surface-elevated border-stroke-low flex max-h-15 min-h-15 items-center justify-between border-t border-l p-2`}><CouponPixButtonClientContainer couponIndex={couponIndexToRender} /><ClearCouponButton couponIndex={couponIndexToRender} /></div> : undefined} />;
      $[34] = bettingEvents;
      $[35] = coupon;
      $[36] = couponIndex;
      $[37] = couponIndicesToRender.length;
      $[38] = defaultEventType;
      $[39] = events;
      $[40] = handleClick;
      $[41] = hasMultipleCoupons;
      $[42] = isSingleRows;
      $[43] = sportType;
      $[44] = t9;
    } else {
      t9 = $[44];
    }
    t8 = couponIndicesToRender.map(t9);
    $[23] = bettingEvents;
    $[24] = coupon;
    $[25] = couponIndex;
    $[26] = couponIndicesToRender;
    $[27] = defaultEventType;
    $[28] = events;
    $[29] = handleClick;
    $[30] = hasMultipleCoupons;
    $[31] = isSingleRows;
    $[32] = sportType;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[45] !== t8) {
    t9 = <div ref={scrollRefElement} className="flex h-full min-w-40 items-stretch lg:min-w-65.5 lg:overflow-x-auto">{t8}</div>;
    $[45] = t8;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  const couponRows = t9;
  let t10;
  if ($[47] !== couponRows || $[48] !== isLargeScreen) {
    t10 = isLargeScreen && <HorizontalScrollArea scrollElementRef={scrollRefElement} leftLabel={"Skrolla v\xE4nster"} rightLabel={"Skrolla h\xF6ger"}>{couponRows}</HorizontalScrollArea>;
    $[47] = couponRows;
    $[48] = isLargeScreen;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  const t11 = !isLargeScreen && couponRows;
  let t12;
  if ($[50] !== t10 || $[51] !== t11) {
    t12 = <div className="relative isolate h-full self-stretch lg:overflow-x-auto">{t10}{t11}</div>;
    $[50] = t10;
    $[51] = t11;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  return t12;
}